// Boot-time health probe. Pings critical PostgREST tables once after the
// session is hydrated. Catches the silent-fail class of bug where the API
// returns 401/403/empty and Forecaster.jsx (etc.) swallow it via
// `data || []`, leaving pages blank with no console signal.
//
// Intentionally not on a polling loop — that's SystemStatus's job. This
// runs ONCE per app boot and surfaces a banner if something looks wrong.
//
// Why these four tables: each backs a top-of-nav surface (Stock, Finance,
// Contacts, app-wide nav). If any returns empty when we know prod has
// rows, something upstream broke (auth, RLS, schema cache, deploy).

import { aws } from './awsdb';

// Each entry: { table, filter, label }.
// `filter` runs as `.eq()` chains. Empty {} = no filter.
// `label` is what we show in the banner.
const CRITICAL_TABLES = [
  { table: 'forecaster_skus',  filter: { is_active: true }, label: 'Stock SKUs' },
  { table: 'contacts',         filter: {},                  label: 'Contacts' },
  { table: 'purchase_orders',  filter: {},                  label: 'Purchase orders' },
  { table: 'staff_directory',  filter: { active: true },    label: 'Staff directory' },
];

const PROBE_TIMEOUT_MS = 8_000;

async function probeOne({ table, filter, label }) {
  let q = aws.from(table).select('*').limit(1);
  for (const [k, v] of Object.entries(filter)) q = q.eq(k, v);

  // Promise.race with a timeout sentinel. We don't abort the query on timeout
  // (postgrest-js builder API for abort varies across versions) — we just
  // stop waiting. Worst case: the request settles into the void; harmless.
  const timeout = new Promise((resolve) =>
    setTimeout(() => resolve({ __timedOut: true }), PROBE_TIMEOUT_MS),
  );

  try {
    const result = await Promise.race([q, timeout]);
    if (result?.__timedOut) {
      return { table, label, kind: 'exception', detail: `timed out after ${PROBE_TIMEOUT_MS}ms` };
    }
    const { data, error } = result;
    if (error) {
      return { table, label, kind: 'error', detail: error.message || error.code || 'unknown' };
    }
    if (!Array.isArray(data) || data.length === 0) {
      return { table, label, kind: 'empty', detail: 'returned 0 rows' };
    }
    return null;
  } catch (e) {
    return { table, label, kind: 'exception', detail: e?.message || String(e) };
  }
}

// Returns an array of issues. Empty array = healthy.
export async function runHealthCheck() {
  const results = await Promise.all(CRITICAL_TABLES.map(probeOne));
  return results.filter(Boolean);
}
